import create from "zustand";

import { InterviewerHarnessType } from "@performyard/service-contracts";

import type { AskPerformYardState } from "./types";

import {
	INTERVIEWER_HARNESS_KEY,
	buildGreetingMessage,
	getInterviewerNotesKey,
	initialDocumentModalState,
	resetSessionId,
} from "./helpers";
import { createChatSlice } from "./slices/chat-slice";
import { createCompetencySlice } from "./slices/competency-slice";
import { createDocumentModalSlice } from "./slices/document-modal-slice";
import { createFeatureSlice } from "./slices/feature-slice";
import { createFormQuestionSlice } from "./slices/form-question-slice";
import { createFormSlice } from "./slices/form-slice";
import { createGoalSlice } from "./slices/goal-slice";
import { createReportSlice } from "./slices/report-slice";
import { createScrollSlice } from "./slices/scroll-slice";
import { createUiSlice } from "./slices/ui-slice";
import { drainPendingChunks, setStoreRef } from "./streaming-buffer";

export const useAskPerformYardStore = create<AskPerformYardState>((set) => ({
	...createUiSlice(set),
	...createChatSlice(set),
	...createFeatureSlice(set),
	...createGoalSlice(set),
	...createFormSlice(set),
	...createFormQuestionSlice(set),
	...createDocumentModalSlice(set),
	...createScrollSlice(set),
	...createCompetencySlice(set),
	...createReportSlice(set),

	// Reset chat state (note: does not reset UI state like isOpen/isExpanded)
	resetChat: () => {
		drainPendingChunks();
		for (const harness of InterviewerHarnessType.options) {
			sessionStorage.removeItem(getInterviewerNotesKey(harness));
		}
		sessionStorage.removeItem(INTERVIEWER_HARNESS_KEY);
		set((state) => ({
			sessionId: resetSessionId(),
			messages: [
				buildGreetingMessage({
					askHrEnabled: state.cachedAskHrEnabled,
					goalCreationEnabled: state.cachedGoalCreationEnabled,
					formCreationEnabled: state.cachedFormCreationEnabled,
					interviewerEnabled: state.cachedInterviewerEnabled,
					reportBuilderEnabled: state.cachedReportBuilderEnabled,
					isOnFormBuilderPage: state.cachedIsOnFormBuilderPage,
				}),
			],
			status: "idle",
			streamStage: null,
			streamMessage: null,
			error: null,
			inputValue: "",
			hasNoDocuments: false,
			goalSuggestionStates: [],
			creatingSuggestionIds: new Set<string>(),
			isCreatingAll: false,
			isGoalEditPanelOpen: false,
			activeEditMessageId: null,
			formSuggestionStates: [],
			creatingFormIds: new Set<string>(),
			formQuestionSuggestionStates: [],
			documentModal: initialDocumentModalState,
			competencyFramework: null,
			isCompetencyEditPanelOpen: false,
			activeInterviewerHarness: null,
			reportBuilder: null,
		}));
	},
}));

// Wire up the lazy store reference for the streaming buffer
setStoreRef(useAskPerformYardStore);
